import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Select,
  Checkbox,
  Spin,
  Form,
  Radio,
  Switch,
  Tooltip,
  Divider,
  Col,
  Row,
} from "antd";

import { httpService } from "../../../../../services/httpService.service";
import { RolesEnum } from "../../../../../services/rolesEnum";

import showConfirm from "../../../../components/ExitConfirm/ExitConfirm";

import AddMemberIcon from "../../../../../Assets/svg/addMemberIcon.svg";
import cancelX from "../../../../../Assets/svg/cancelXbutton.svg";

import "../../../UserManagement/UserManagement.scss";

const existingRoleIds = Object.entries(RolesEnum)
  .filter(
    ([key]) =>
      key !== "LimitedGuest" && key !== "Guest" && key !== "SystemAdmin"
  )
  .map(([_, value]) => value);

const { Option } = Select;

const AddTeamCardModal = ({
  visible,
  onClose,
  fetchTeams,
  setMessage,
  record,
  editing = false,
}) => {
  const [form] = Form.useForm();

  const [users, setUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [searchTerms, setSearchTerms] = useState({});
  const [searching, setSearching] = useState({});
  const [loadingCities, setLoadingCities] = useState(false);
  const currentContractId = localStorage.getItem("currentContractId");
  const [drivers, setDrivers] = useState([]);
  const [selectedEnumerators, setSelectedEnumerators] = useState([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);

  const hasTeamLeaders = (users["Field Team Leader"] || []).length > 0;

  // Some projects have no Field Team Leader at all - there the Project Manager
  // acts as the team leader, so the requirement moves onto that role.
  const isActingTeamLeader = (role) =>
    role === "Project Manager" && !hasTeamLeaders;

  const isRequiredRole = (role) =>
    role === "Field Coordinator" ||
    role === "Field Team Leader" ||
    isActingTeamLeader(role);

  const handleCancel = () => {
    showConfirm(handleLeave, handleReturn);
  };

  const handleLeave = () => {
    onClose();
    setCities([]);
    setStates([]);
    form.resetFields();

    setSearchTerms({});
    setSearching({});
  };

  const handleReturn = () => {
    // console.log("Cancel action");
  };

  const fetchUsers = async () => {
    await httpService.get(
      `/api/User/getAllusers?contractIds=${currentContractId}&IgnorePagination=true`,
      (res) => {
        const groupedUsers = res.data.reduce((acc, user) => {
          const role = user.userContractRoles.find((role) =>
            existingRoleIds.includes(role.role.id)
          );
          if (role) {
            const roleName = role.role.roleName;
            if (!acc[roleName]) {
              acc[roleName] = [];
            }
            acc[roleName].push(user);
          }
          return acc;
        }, {});

        setUsers(groupedUsers);
      }
    );
  };

  const fetchDrivers = async () => {
    await httpService.get(
      `/api/Driver/getAll?contractId=${currentContractId}&ignorePagination=true`,
      (res) => {
        setDrivers(res.data);
      },
      (error) => {
        console.error("Error fetching drivers:", error);
      }
    );
  };

  useEffect(() => {
    if (!visible) {
      fetchDrivers();
      fetchUsers();
      fetchCountries();
      return;
    }

    if (editing && record?.id) {
      fetchTeamById(record.id);
    }
  }, [visible]);

  // Update selectedEnumerators when form is reset or loaded (e.g. after fetchTeamById)
  useEffect(() => {
    setSelectedEnumerators(form.getFieldValue("enumeratorIds") || []);
  }, [visible]);

  useEffect(() => {
    if (visible) {
      if (editing && record?.id) {
        // fetchTeamById will set selectedDriverIds and selectedEnumerators after data loads
      } else {
        setSelectedDriverIds([]);
        setSelectedEnumerators([]);
      }
    }
  }, [visible, editing, record]);

  const fetchTeamById = (teamId) => {
    setLoading(true);
    httpService.get(
      `/api/User/getTeamById?teamId=${teamId}`,
      (res) => {
        const team = res.data;
        const teamMembers = team?.members;
        const enumerators = teamMembers.filter(
          (member) => member.roleName === "Field Enumerator"
        );
        const driverIds = team?.drivers?.map((driver) => driver?.id);
        form.setFieldsValue({
          teamCardName: team.name || "",
          enumeratorIds: enumerators.map((enumerator) => enumerator.id) || [],
          fieldteamleaderId:
            teamMembers.find(
              (member) => member.roleName === "Field Team Leader"
            )?.id || null,
          dataanalystId: teamMembers.find(
            (member) => member.roleName === "Data Analyst"
          )?.id,
          projectmanagerId: teamMembers.find(
            (member) => member.roleName === "Project Manager"
          )?.id,
          fieldlogisticsId: teamMembers.find(
            (member) => member.roleName === "Field Logistics"
          )?.id,
          fieldcoordinatorId:
            teamMembers.find(
              (member) => member.roleName === "Field Coordinator"
            )?.id || null,
          driverIds: driverIds,
          isActive: team.isActive,
          countryId: team.state?.country.id || null,
          cityId: team.location?.id || null,
          stateId: team.state?.id || null,
        });
        setSelectedDriverIds(driverIds || []);
        setSelectedEnumerators(
          enumerators.map((enumerator) => enumerator.id) || []
        );
        fetchStates(team.state?.country.id);
        fetchCities(team.state?.id);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
      }
    );
  };

  const fetchCountries = async () => {
    const contractId = localStorage.getItem("currentContractId");
    httpService.get(
      `/api/GeoData/getCountriesOfContract?contractId=${contractId}`,
      (res) => {
        setAllCountries(res.data);
      },
      (error) => {
        // console.log(error);
      }
    );
  };

  const fetchStates = () => {
    setLoadingCities(true);
    httpService.get(
      `/api/GeoData/states?countryIds=${[form.getFieldValue("countryId")]}`,
      (res) => {
        setStates(res?.data);
        setLoadingCities(false);
      },
      (error) => {
        setStates([]);
        setCities([]);
        setLoadingCities(false);
      }
    );
  };

  const fetchCities = async () => {
    setLoadingCities(true);
    const stateId = form.getFieldValue("stateId");

    if (stateId) {
      httpService.post(
        `/api/GeoData/cities`,
        [stateId],
        (res) => {
          setCities(res?.data);
          setLoadingCities(false);
        },
        (error) => {
          setLoadingCities(false);
        }
      );
    } else {
      setCities([]);
      setLoadingCities(false);
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    form
      .validateFields()
      .then((values) => {
        const memberIds = [
          ...(selectedEnumerators || []),
          ...[
            values.fieldcoordinatorId,
            values.dataanalystId,
            values.fieldlogisticsId,
            values.fieldteamleaderId,
            values.projectmanagerId,
          ].filter((id) => id != null),
        ];
        const driverIds = selectedDriverIds;
        const payload = {
          name: values.teamCardName,
          contractId: currentContractId,
          memberIds: memberIds,
          driverIds: driverIds,
          isActive: values.isActive,
          countryId: values.countryId,
          cityId: values.cityId,
          stateId: values.stateId,
        };
        if (editing) {
          payload.id = record.id;
        }
        const apiUrl = editing
          ? "/api/User/updateTeam"
          : "/api/User/createTeam";

        const method = editing ? "put" : "post";
        httpService[method](
          apiUrl,
          payload,
          (response) => {
            onClose();
            const message = response?.data?.message;

            setMessage({
              trigger: true,
              type: "success",
              icon: AddMemberIcon,
              title: `Team card ${editing ? "updated" : "added"} successfully!`,
              text:
                message ||
                `Team card has been ${
                  editing ? "updated" : "added"
                } to your database!`,
            });
            setIsSubmitting(false);
            fetchTeams(currentContractId);
            form.resetFields();
            setSelectedDriverIds([]);
            setSelectedEnumerators([]);
            setCities([]);
            setStates([]);
            setTimeout(() => {
              setMessage({
                trigger: false,
              });
            }, 3000);
          },
          (error) => {
            setMessage({
              trigger: true,
              type: "danger",
              icon: AddMemberIcon,
              title: `Failed to ${editing ? "edit" : "add"} the team card`,
              text:
                error?.errors?.[0]?.message ||
                error?.message ||
                `We had a problem ${
                  editing ? "editing" : "adding"
                } this team card to your database, please try again!`,
            });
            setTimeout(() => {
              setMessage({
                trigger: false,
              });
            }, 3000);
            setIsSubmitting(false);
          }
        );
      })
      .catch((errorInfo) => {
        scrollToField(form, errorInfo);
      });
  };

  const scrollToField = (form, errorInfo) => {
    const firstErrorField = errorInfo?.errorFields?.[0];
    if (firstErrorField) {
      form.scrollToField(firstErrorField?.name[0], {
        behavior: "smooth",
        block: "center",
      });
    }
  };

  function useDebounce(callback, delay) {
    const timeoutRef = useRef(null); // ✅ THIS LINE IS IMPORTANT

    const debouncedFn = (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    };

    useEffect(() => {
      return () => {
        // Clean up timeout on unmount
        clearTimeout(timeoutRef.current);
      };
    }, []);

    return debouncedFn;
  }

  const handleSearchChange = useDebounce((role, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [role]: value,
    }));

    setSearching((prev) => ({
      ...prev,
      [role]: false,
    }));
  }, 500);

  const handleInputChange = (role, value) => {
    setSearching((prev) => ({
      ...prev,
      [role]: true,
    }));

    handleSearchChange(role, value);
  };

  console.log(form.getFieldsValue());

  return (
    <Modal
      visible={visible}
      closable={false}
      onCancel={handleCancel}
      footer={false}
      width={800}
      destroyOnClose
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
        }}
      >
        <img
          src={cancelX}
          style={{ marginLeft: "auto", cursor: "pointer" }}
          onClick={handleCancel}
          alt="Close"
        />
      </div>
      <h2 className="modalHeader">Team Card</h2>
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="membersForm"
        >
          <p className="modalsHeader1">General Details</p>

          <Form.Item
            name="teamCardName"
            label="Team Card Name"
            rules={[
              {
                required: !editing,
                message: "Please enter Team Card Name",
              },
            ]}
          >
            <Input
              disabled={editing}
              name="teamCardName"
              placeholder="Enter Team Card Name"
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="countryId"
            label="Country"
            rules={[{ required: !editing, message: "Please select a country" }]}
          >
            <Select
              disabled={editing}
              showSearch
              allowClear
              placeholder="Select Country"
              onChange={(value) => {
                fetchStates();
                form.setFieldsValue({
                  countryId: value,
                  stateId: null,
                  cityId: null,
                });
              }}
              value={form.getFieldValue("countryId")}
              filterOption={(input, option) =>
                option.children.toLowerCase().startsWith(input.toLowerCase())
              }
            >
              {allCountries.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.stateName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Spin spinning={loadingCities}>
            <Form.Item
              name="stateId"
              label={
                cities.length < 1
                  ? "Governorate/State or City"
                  : "Governorate/State"
              }
              rules={[{ required: !editing, message: "Please select a state" }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select State"
                disabled={
                  editing ||
                  !form.getFieldValue("countryId") ||
                  states.length < 1
                }
                onChange={(value) => {
                  fetchCities();
                  form.setFieldsValue({
                    stateId: value,
                    cityId: null,
                  });
                }}
                value={form.getFieldValue("stateId")}
                filterOption={(input, option) =>
                  option?.children.toLowerCase().startsWith(input.toLowerCase())
                }
              >
                {states?.map((s) => (
                  <Option key={s?.id} value={s?.id}>
                    {s?.stateName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {form.getFieldValue("stateId") && cities.length > 0 && (
              <Form.Item
                name="cityId"
                label="City"
                rules={[
                  {
                    required: cities?.length && !editing,
                    message: "Please select a city",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select City"
                  disabled={
                    editing ||
                    !form.getFieldValue("stateId") ||
                    cities?.length === 0
                  }
                  onChange={(value) => {
                    form.setFieldsValue({
                      cityId: value,
                    });
                  }}
                  value={form.getFieldValue("cityId")}
                  filterOption={(input, option) =>
                    option?.children
                      .toLowerCase()
                      .startsWith(input.toLowerCase())
                  }
                >
                  {cities?.map((c) => (
                    <Option key={c?.id} value={c?.id}>
                      {c?.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Spin>
          <Divider />
          <div style={{ paddingBottom: "20px" }}>
            <label
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "22px",
              }}
            >
              Select Team Members
            </label>
          </div>
          {Object.entries(users)?.map(([role, roleUsers], index, array) =>
            role !== "Field Enumerator" ? (
              <>
                <label className="mb-3 text-capitalize">
                  {isRequiredRole(role) ? (
                    <span style={{ color: "red", paddingRight: "3px" }}>*</span>
                  ) : (
                    ""
                  )}
                  {isActingTeamLeader(role)
                    ? "Select project manager (acting as field team leader)"
                    : `Select ${role.toLowerCase()}`}
                </label>
                <Input.Search
                  placeholder={`Search ${role.toLowerCase()}s`}
                  allowClear
                  onChange={(e) => {
                    e.preventDefault();
                    handleInputChange(role, e.target.value);
                  }}
                  style={{ marginBottom: 20 }}
                />
                <Spin spinning={searching[role] || false}>
                  <Form.Item
                    className="text-capitalize"
                    name={`${role.toLowerCase().replace(/\s+/g, "")}Id`}
                    key={role}
                    rules={
                      isRequiredRole(role)
                        ? [
                            {
                              required: true,
                              message: `Please select a ${role.toLowerCase()}`,
                            },
                          ]
                        : []
                    }
                  >
                    {roleUsers && roleUsers.length > 0 ? (
                      <Radio.Group>
                        <Row gutter={[16, 16]}>
                          {roleUsers
                            .filter((user) => {
                              const name =
                                (user.firstName ||
                                  user.userDetails?.firstName) &&
                                (user.lastName || user.userDetails?.lastName)
                                  ? `${
                                      user.firstName ||
                                      user.userDetails?.firstName
                                    } ${
                                      user.lastName ||
                                      user.userDetails?.lastName
                                    }`
                                  : user.userName || user.email;

                              return name
                                .toLowerCase()
                                .startsWith(
                                  (searchTerms[role] || "").toLowerCase()
                                );
                            })
                            .map((user) => {
                              const userName =
                                (user.firstName ||
                                  user.userDetails?.firstName) &&
                                (user.lastName || user.userDetails?.lastName)
                                  ? `${
                                      user.firstName ||
                                      user.userDetails?.firstName
                                    } ${
                                      user.lastName ||
                                      user.userDetails?.lastName
                                    }`
                                  : user.userName || user.email;

                              return (
                                <Col xs={24} md={12} lg={12} key={user.id}>
                                  <Radio value={user.id}>
                                    <Tooltip
                                      title={userName}
                                      placement="topLeft"
                                    >
                                      <p
                                        style={{ maxWidth: "100%" }}
                                        className="text-nowrap text-truncate m-0"
                                      >
                                        {userName}
                                      </p>
                                    </Tooltip>
                                  </Radio>
                                </Col>
                              );
                            })}
                        </Row>
                      </Radio.Group>
                    ) : (
                      <p>No {role.toLowerCase()}s available!</p>
                    )}
                  </Form.Item>
                </Spin>

                {index < array.length - 1 && <Divider />}
              </>
            ) : null
          )}
          <Divider />

          <label className="mb-3">Select Driver</label>
          <Input.Search
            placeholder="Search Drivers"
            allowClear
            onChange={(e) => {
              e.preventDefault();
              handleInputChange("Driver", e.target.value);
            }}
            style={{ marginBottom: 20 }}
          />
          <Spin
            spinning={searching["Driver"] ? searching["Driver"] : false}
            className="container"
          >
            <Form.Item
              name="driverIds"
              className="w-100"
              // rules={[{ required: true, message: "Please select a driver" }]}
            >
              <Checkbox.Group
                value={selectedDriverIds}
                onChange={(checkedValues) => {
                  const visibleDriverIds = drivers
                    .filter((user) =>
                      user.name
                        .toLowerCase()
                        .startsWith((searchTerms["Driver"] || "").toLowerCase())
                    )
                    .map((driver) => driver.id);
                  const preserved = selectedDriverIds.filter(
                    (id) => !visibleDriverIds.includes(id)
                  );
                  const newValue = [...preserved, ...checkedValues];
                  setSelectedDriverIds(newValue);
                  form.setFieldsValue({ driverIds: newValue });
                }}
              >
                <Row gutter={[16, 16]}>
                  {drivers && drivers.length > 0 ? (
                    drivers
                      .filter((user) => {
                        const name = user.name;
                        return name
                          .toLowerCase()
                          .startsWith(
                            (searchTerms["Driver"] || "").toLowerCase()
                          );
                      })
                      .map((driver) => (
                        <Col xs={24} md={12} lg={12} key={driver.id}>
                          <Checkbox value={driver.id}>
                            <Tooltip title={driver.name} placement="topLeft">
                              <p
                                style={{ maxWidth: "250px" }}
                                className="text-nowrap text-truncate m-0"
                              >
                                {driver.name || "No name provided"}
                              </p>
                            </Tooltip>
                          </Checkbox>
                        </Col>
                      ))
                  ) : (
                    <p>No drivers available!</p>
                  )}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </Spin>
          <Divider />
          <label className="mb-3">
            <span style={{ color: "red", paddingRight: "3px" }}>*</span>
            Select Enumerator
          </label>
          <Input.Search
            placeholder="Search Enumerators"
            allowClear
            onChange={(e) => {
              e.preventDefault();
              handleInputChange("Field Enumerator", e.target.value);
            }}
            style={{ marginBottom: 20 }}
          />
          <Spin
            spinning={
              searching["Field Enumerator"]
                ? searching["Field Enumerator"]
                : false
            }
          >
            <Form.Item
              label="Select Enumerators"
              name="enumeratorIds"
              rules={[
                { required: true, message: "Please select enumerator(s)" },
              ]}
            >
              <Checkbox.Group
                value={selectedEnumerators}
                onChange={(checkedValues) => {
                  const visibleIds = (users["Field Enumerator"] || [])
                    .filter((user) => {
                      const name =
                        (user.firstName || user.userDetails?.firstName) &&
                        (user.lastName || user.userDetails?.lastName)
                          ? `${user.firstName || user.userDetails?.firstName} ${
                              user.lastName || user.userDetails?.lastName
                            }`
                          : user.userName || user.email;
                      return name
                        .toLowerCase()
                        .startsWith(
                          (searchTerms["Field Enumerator"] || "").toLowerCase()
                        );
                    })
                    .map((user) => user.id);
                  const preserved = selectedEnumerators.filter(
                    (id) => !visibleIds.includes(id)
                  );
                  const newSelected = [...preserved, ...checkedValues];
                  setSelectedEnumerators(newSelected);
                  form.setFieldsValue({ enumeratorIds: newSelected });
                }}
              >
                <Row gutter={[16, 16]}>
                  {users["Field Enumerator"] &&
                  users["Field Enumerator"].length > 0 ? (
                    users["Field Enumerator"]
                      .filter((user) => {
                        const name =
                          (user.firstName || user.userDetails?.firstName) &&
                          (user.lastName || user.userDetails?.lastName)
                            ? `${
                                user.firstName || user.userDetails?.firstName
                              } ${user.lastName || user.userDetails?.lastName}`
                            : user.userName || user.email;
                        return name
                          .toLowerCase()
                          .startsWith(
                            (
                              searchTerms["Field Enumerator"] || ""
                            ).toLowerCase()
                          );
                      })
                      .map((user) => {
                        const userName =
                          (user.firstName || user.userDetails?.firstName) &&
                          (user.lastName || user.userDetails?.lastName)
                            ? `${
                                user.firstName || user.userDetails?.firstName
                              } ${user.lastName || user.userDetails?.lastName}`
                            : user.userName || user.email;
                        return (
                          <Col xs={24} md={12} lg={12} key={user.id}>
                            <Checkbox value={user.id}>
                              <Tooltip title={userName} placement="topLeft">
                                <p
                                  style={{ maxWidth: "250px" }}
                                  className="text-nowrap text-truncate m-0"
                                >
                                  {userName}
                                </p>
                              </Tooltip>
                            </Checkbox>
                          </Col>
                        );
                      })
                  ) : (
                    <p>No enumerators available!</p>
                  )}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </Spin>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              paddingBottom: "20px",
              paddingTop: "20px",
            }}
          >
            <div
              style={{
                borderRadius: "6px",
                padding: "1px 0px 1px 10px",
                gap: "10px",
                background: "#FADCEA",
              }}
            >
              <h1
                style={{
                  color: "var(--2, #AA1A5F)",
                  fontFamily: "Lato",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "22px",
                  margin: 0,
                }}
              >
                Note: You can only choose
                <span style={{ fontWeight: 700 }}> one </span>Field Coordinator.
              </h1>
            </div>
          </div>
          <Form.Item
            name="isActive"
            className="ms-2"
            valuePropName="checked"
            label="Is Team Active"
            layout="horizontal"
          >
            <Switch />
          </Form.Item>
          <div className="buttonsModal">
            <Button
              className="inviteButtonTPM"
              key="cancel"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              className="addButtonTPM"
              key="submit"
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
            >
              {editing ? "Edit" : "Add"} Team Card
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddTeamCardModal;
